import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Globe } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

const PERMISSION_DEEPLINKS = {
  manage_events: '/events',
  publish_events: '/events',
  manage_announcements: '/announcements',
  publish_announcements: '/announcements',
  manage_success_stories: '/success-stories',
  publish_success_stories: '/success-stories',
  edit_manager_profile: '/hub-editor',
  edit_kpis: '/hub-editor',
  edit_faq: '/hub-editor',
  edit_support: '/hub-editor',
  edit_branding: '/hub-editor',
  publish_hub: '/hub-editor',
  view_partners_in_country: '/country',
  contact_partners: '/country',
  generate_trial_codes: '/country',
}

const PERMISSION_LABELS = {
  manage_events: 'Crear y editar eventos',
  publish_events: 'Publicar eventos',
  manage_announcements: 'Crear y editar anuncios',
  publish_announcements: 'Publicar anuncios',
  manage_success_stories: 'Crear y editar casos de éxito',
  publish_success_stories: 'Publicar casos de éxito',
  edit_manager_profile: 'Editar perfil del Country Manager',
  edit_kpis: 'Editar KPIs',
  edit_faq: 'Editar preguntas frecuentes',
  edit_support: 'Editar información de soporte',
  edit_branding: 'Editar branding',
  publish_hub: 'Publicar cambios del Country Hub',
  generate_trial_codes: 'Generar códigos de prueba',
  view_partners_in_country: 'Ver partners del país',
  contact_partners: 'Contactar partners del país',
  invite_team_members: 'Invitar miembros al equipo',
  manage_team_permissions: 'Administrar permisos del equipo',
}

export default function TeamWelcomeModal() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { teamMember, partner, refreshSession } = useAuthStore()
  const [open, setOpen] = useState(false)
  const [session, setSession] = useState(null)

  useEffect(() => {
    const welcome = searchParams.get('welcome')
    if (welcome !== 'team') return

    let cancelled = false
    ;(async () => {
      let s = teamMember ? { teamMember, partner } : null
      if (!s) {
        const fetched = await refreshSession()
        if (!cancelled && fetched) {
          s = { teamMember: fetched.teamMember, partner: fetched.partner }
        }
      }
      if (!s?.teamMember) return

      const seenKey = `partner_team_welcome_seen_${s.teamMember._id}`
      if (localStorage.getItem(seenKey)) {
        searchParams.delete('welcome')
        setSearchParams(searchParams, { replace: true })
        return
      }
      if (!cancelled) {
        setSession(s)
        setOpen(true)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('welcome')])

  const handleClose = () => {
    if (session?.teamMember?._id) {
      localStorage.setItem(`partner_team_welcome_seen_${session.teamMember._id}`, '1')
    }
    setOpen(false)
    searchParams.delete('welcome')
    setSearchParams(searchParams, { replace: true })
  }

  const handleNavigate = (path) => {
    handleClose()
    navigate(path)
  }

  if (!open || !session?.teamMember) return null

  const { teamMember: member, partner: p } = session
  const granted = member.permissions
    ? Object.keys(member.permissions).filter((k) => member.permissions[k])
    : []

  const sectionsSet = new Set()
  const sections = []
  granted.forEach((k) => {
    const path = PERMISSION_DEEPLINKS[k]
    if (path && !sectionsSet.has(path)) {
      sectionsSet.add(path)
      sections.push({ path, label: PERMISSION_LABELS[k] || k })
    }
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Globe size={20} className="text-primary" />
            <DialogTitle>¡Bienvenido al Country Hub!</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
              Ahora formas parte del equipo de{' '}
              <strong className="text-neutral-900 dark:text-neutral-50">
                {p?.organizationName || 'CoreDesk'}
              </strong>
              {p?.managedCountry ? ` (${p.managedCountry})` : ''}.
            </p>
            {member.invitePreset && (
              <Badge variant="default" className="mt-2">
                Rol: {member.jobTitle || member.invitePreset}
              </Badge>
            )}
          </div>

          {granted.length > 0 ? (
            <div>
              <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50 mb-1.5">
                Lo que puedes hacer
              </p>
              <ul className="space-y-1">
                {granted.slice(0, 8).map((k) => (
                  <li
                    key={k}
                    className="flex items-start gap-2 text-body-sm text-neutral-700 dark:text-neutral-300"
                  >
                    <CheckCircle2
                      size={14}
                      className="text-success shrink-0 mt-0.5"
                    />
                    {PERMISSION_LABELS[k] || k}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-body-sm text-neutral-500 dark:text-neutral-400">
              Tu rol es de solo lectura. Puedes navegar el Country Hub pero no editarlo.
            </p>
          )}

          {sections.length > 0 && (
            <div>
              <p className="text-body-sm font-semibold text-neutral-900 dark:text-neutral-50 mb-2">
                Empieza por aquí
              </p>
              <div className="flex flex-wrap gap-2">
                {sections.slice(0, 4).map((s) => (
                  <Button
                    key={s.path}
                    variant="outline"
                    size="sm"
                    onClick={() => handleNavigate(s.path)}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={handleClose}>Empezar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
